import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const RegistrationForm = ({ onSubmit, isLoading }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    nationality: '',
    country: '',
    address: '',
    city: '',
    postalCode: '',
    occupation: '',
    sourceOfIncome: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false,
    agreeToPrivacy: false,
    agreeToMarketing: false
  });

  const [errors, setErrors] = useState({});
  const [passwordStrength, setPasswordStrength] = useState(0);

  const countryOptions = [
    { value: 'US', label: 'United States' },
    { value: 'GB', label: 'United Kingdom' },
    { value: 'DE', label: 'Germany' },
    { value: 'FR', label: 'France' },
    { value: 'IT', label: 'Italy' },
    { value: 'ES', label: 'Spain' },
    { value: 'NL', label: 'Netherlands' },
    { value: 'BE', label: 'Belgium' },
    { value: 'AU', label: 'Australia' },
    { value: 'CA', label: 'Canada' }
  ];

  const occupationOptions = [
    { value: 'employed', label: 'Employed' },
    { value: 'self-employed', label: 'Self-Employed' },
    { value: 'business-owner', label: 'Business Owner' },
    { value: 'student', label: 'Student' },
    { value: 'retired', label: 'Retired' },
    { value: 'unemployed', label: 'Unemployed' },
    { value: 'other', label: 'Other' }
  ];

  const sourceOfIncomeOptions = [
    { value: 'salary', label: 'Salary/Wages' },
    { value: 'business-income', label: 'Business Income' },
    { value: 'investments', label: 'Investments' },
    { value: 'pension', label: 'Pension' },
    { value: 'savings', label: 'Savings' },
    { value: 'family-support', label: 'Family Support' },
    { value: 'other', label: 'Other' }
  ];

  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password?.length >= 8) strength += 25;
    if (/[A-Z]/?.test(password)) strength += 25;
    if (/[0-9]/?.test(password)) strength += 25;
    if (/[^A-Za-z0-9]/?.test(password)) strength += 25;
    return strength;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (field === 'password') {
      setPasswordStrength(calculatePasswordStrength(value));
    }
    
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required field validation
    if (!formData?.firstName?.trim()) newErrors.firstName = 'First name is required';
    if (!formData?.lastName?.trim()) newErrors.lastName = 'Last name is required';
    if (!formData?.email?.trim()) newErrors.email = 'Email is required';
    if (!formData?.phone?.trim()) newErrors.phone = 'Phone number is required';
    if (!formData?.dateOfBirth) newErrors.dateOfBirth = 'Date of birth is required';
    if (!formData?.nationality) newErrors.nationality = 'Nationality is required';
    if (!formData?.country) newErrors.country = 'Country of residence is required';
    if (!formData?.address?.trim()) newErrors.address = 'Address is required';
    if (!formData?.city?.trim()) newErrors.city = 'City is required';
    if (!formData?.postalCode?.trim()) newErrors.postalCode = 'Postal code is required';
    if (!formData?.occupation) newErrors.occupation = 'Occupation is required';
    if (!formData?.sourceOfIncome) newErrors.sourceOfIncome = 'Source of income is required';
    if (!formData?.password) newErrors.password = 'Password is required';
    if (!formData?.confirmPassword) newErrors.confirmPassword = 'Please confirm your password';

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData?.email && !emailRegex?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Phone validation
    const phoneRegex = /^\+?[\d\s\-\(\)]{10,}$/;
    if (formData?.phone && !phoneRegex?.test(formData?.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    // Age validation (must be 18+)
    if (formData?.dateOfBirth) {
      const birthDate = new Date(formData.dateOfBirth);
      const today = new Date();
      const age = today?.getFullYear() - birthDate?.getFullYear();
      if (age < 18) {
        newErrors.dateOfBirth = 'You must be at least 18 years old';
      }
    }

    // Password validation
    if (formData?.password && formData?.password?.length < 8) {
      newErrors.password = 'Password must be at least 8 characters long';
    }

    if (formData?.password && passwordStrength < 75) {
      newErrors.password = 'Password must contain uppercase, lowercase, number, and special character';
    }

    // Confirm password validation
    if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // Terms and conditions validation
    if (!formData?.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the Terms and Conditions';
    }

    if (!formData?.agreeToPrivacy) {
      newErrors.agreeToPrivacy = 'You must agree to the Privacy Policy';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength < 25) return 'bg-error';
    if (passwordStrength < 50) return 'bg-warning';
    if (passwordStrength < 75) return 'bg-accent';
    return 'bg-success';
  };

  const getPasswordStrengthText = () => {
    if (passwordStrength < 25) return 'Weak';
    if (passwordStrength < 50) return 'Fair';
    if (passwordStrength < 75) return 'Good';
    return 'Strong';
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Personal Information Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
          <Icon name="User" size={20} />
          <span>Personal Information</span>
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="First Name"
            type="text"
            placeholder="Enter your first name"
            value={formData?.firstName}
            onChange={(e) => handleInputChange('firstName', e?.target?.value)}
            error={errors?.firstName}
            required
          />
          
          <Input
            label="Last Name"
            type="text"
            placeholder="Enter your last name"
            value={formData?.lastName}
            onChange={(e) => handleInputChange('lastName', e?.target?.value)}
            error={errors?.lastName}
            required
          />
        </div>

        <Input
          label="Email Address"
          type="email"
          placeholder="Enter your email address"
          value={formData?.email}
          onChange={(e) => handleInputChange('email', e?.target?.value)}
          error={errors?.email}
          description="We'll use this email for account verification and important notifications"
          required
        />

        <Input
          label="Phone Number"
          type="tel"
          placeholder="+1 (555) 123-4567"
          value={formData?.phone}
          onChange={(e) => handleInputChange('phone', e?.target?.value)}
          error={errors?.phone}
          description="Include country code for international numbers"
          required
        />

        <Input
          label="Date of Birth"
          type="date"
          value={formData?.dateOfBirth}
          onChange={(e) => handleInputChange('dateOfBirth', e?.target?.value)}
          error={errors?.dateOfBirth}
          description="You must be at least 18 years old to register"
          required
        />

        <Select
          label="Nationality"
          placeholder="Select your nationality"
          options={countryOptions}
          value={formData?.nationality}
          onChange={(value) => handleInputChange('nationality', value)}
          error={errors?.nationality}
          searchable
          required
        />
      </div>
      {/* Address Information Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
          <Icon name="MapPin" size={20} />
          <span>Address Information</span>
        </h3>

        <Select
          label="Country of Residence"
          placeholder="Select your country"
          options={countryOptions}
          value={formData?.country}
          onChange={(value) => handleInputChange('country', value)}
          error={errors?.country}
          searchable
          required
        />

        <Input
          label="Street Address"
          type="text"
          placeholder="Enter your full address"
          value={formData?.address}
          onChange={(e) => handleInputChange('address', e?.target?.value)}
          error={errors?.address}
          required
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="City"
            type="text"
            placeholder="Enter your city"
            value={formData?.city}
            onChange={(e) => handleInputChange('city', e?.target?.value)}
            error={errors?.city}
            required
          />
          
          <Input
            label="Postal Code"
            type="text"
            placeholder="Enter postal code"
            value={formData?.postalCode}
            onChange={(e) => handleInputChange('postalCode', e?.target?.value)}
            error={errors?.postalCode}
            required
          />
        </div>
      </div>
      {/* Employment Information Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
          <Icon name="Briefcase" size={20} />
          <span>Employment Information</span>
        </h3>

        <Select
          label="Occupation"
          placeholder="Select your occupation"
          options={occupationOptions}
          value={formData?.occupation}
          onChange={(value) => handleInputChange('occupation', value)}
          error={errors?.occupation}
          description="Required for compliance and risk assessment"
          required
        />

        <Select
          label="Source of Income"
          placeholder="Select your primary source of income"
          options={sourceOfIncomeOptions}
          value={formData?.sourceOfIncome}
          onChange={(value) => handleInputChange('sourceOfIncome', value)}
          error={errors?.sourceOfIncome}
          description="This information helps us comply with anti-money laundering regulations"
          required
        />
      </div>
      {/* Security Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
          <Icon name="Shield" size={20} />
          <span>Account Security</span>
        </h3>

        <div className="space-y-2">
          <Input
            label="Password"
            type="password"
            placeholder="Create a strong password"
            value={formData?.password}
            onChange={(e) => handleInputChange('password', e?.target?.value)}
            error={errors?.password}
            description="Must contain at least 8 characters with uppercase, lowercase, number, and special character"
            required
          />
          
          {formData?.password && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Password Strength:</span>
                <span className={`font-medium ${
                  passwordStrength < 50 ? 'text-error' : 
                  passwordStrength < 75 ? 'text-warning' : 'text-success'
                }`}>
                  {getPasswordStrengthText()}
                </span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div 
                  className={`h-2 rounded-full transition-all duration-300 ${getPasswordStrengthColor()}`}
                  style={{ width: `${passwordStrength}%` }}
                />
              </div>
            </div>
          )}
        </div>

        <Input
          label="Confirm Password"
          type="password"
          placeholder="Confirm your password"
          value={formData?.confirmPassword}
          onChange={(e) => handleInputChange('confirmPassword', e?.target?.value)}
          error={errors?.confirmPassword}
          required
        />
      </div>
      {/* Terms and Conditions Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
          <Icon name="FileText" size={20} />
          <span>Legal Agreements</span>
        </h3>

        <div className="space-y-3">
          <Checkbox
            label="I agree to the Terms and Conditions"
            description="By checking this box, you agree to our terms of service and user agreement"
            checked={formData?.agreeToTerms}
            onChange={(e) => handleInputChange('agreeToTerms', e?.target?.checked)}
            error={errors?.agreeToTerms}
            required
          />

          <Checkbox
            label="I agree to the Privacy Policy"
            description="You consent to our collection and use of your personal data as described in our privacy policy"
            checked={formData?.agreeToPrivacy}
            onChange={(e) => handleInputChange('agreeToPrivacy', e?.target?.checked)}
            error={errors?.agreeToPrivacy}
            required
          />

          <Checkbox
            label="I would like to receive marketing communications"
            description="Optional: Receive updates about new features, promotions, and money transfer tips"
            checked={formData?.agreeToMarketing}
            onChange={(e) => handleInputChange('agreeToMarketing', e?.target?.checked)}
          />
        </div>
      </div>
      {/* Submit Button */}
      <div className="pt-4">
        <Button
          type="submit"
          variant="default"
          size="lg"
          fullWidth
          loading={isLoading}
          iconName="ArrowRight"
          iconPosition="right"
        >
          Create Account & Continue
        </Button>
      </div>
    </form>
  );
};

export default RegistrationForm;